import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Children, type ReactNode, Suspense, useEffect, useRef, useState } from 'react'

import AnimatedLynkIcon from 'src/features/lynk/components/animated-lynk-icon'

import { useLynkSearch } from '../api/use-lynk-search'
import BusinessSearchResultItem from '../components/business-search-result-item'
import LynkUserSearchResultItem from '../components/lynk-user-search-result-item'

interface AISearchResultsViewProps {
  committedQuery: string
}

const ThinkingState = () => {
  const [dotCount, setDotCount] = useState(1)

  useEffect(() => {
    const interval = window.setInterval(() => {
      setDotCount((prev) => (prev === 3 ? 1 : prev + 1))
    }, 350)

    return () => window.clearInterval(interval)
  }, [])

  return (
    <div className="flex min-h-[300px] flex-col gap-2 pt-2">
      <div className="flex flex-row items-center gap-2">
        <AnimatedLynkIcon autoPlay={true} variant="gradient" width={26} height={26} />
        <p className="text-[14px] font-semibold text-primary">{`Thinking${'.'.repeat(dotCount)}`}</p>
      </div>
    </div>
  )
}

const SCROLL_BY_PX = 260

interface HorizontalSectionProps {
  title: string
  children: ReactNode
}

const HorizontalSection = ({ title, children }: HorizontalSectionProps) => {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const hasItems = Children.count(children) > 0

  const updateArrows = () => {
    const node = scrollerRef.current
    if (!node) return

    setCanScrollLeft(node.scrollLeft > 0)
    setCanScrollRight(node.scrollLeft + node.clientWidth < node.scrollWidth - 1)
  }

  useEffect(() => {
    updateArrows()
    window.addEventListener('resize', updateArrows)
    return () => window.removeEventListener('resize', updateArrows)
  }, [])

  return (
    <div className="relative flex flex-col gap-3">
      <p className="text-[15px] font-semibold text-primary">{title}</p>
      <div
        ref={scrollerRef}
        onScroll={updateArrows}
        className="flex snap-x snap-mandatory flex-row gap-4 overflow-x-auto pb-1 scrollbar-none"
      >
        {children}
      </div>

      {hasItems && (
        <button
          type="button"
          onClick={() => scrollerRef.current?.scrollBy({ left: -SCROLL_BY_PX, behavior: 'smooth' })}
          className="absolute left-0 top-[52%] z-10 flex size-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 text-primary shadow"
          aria-label={`Scroll ${title} left`}
        >
          <ChevronLeft className={canScrollLeft ? 'opacity-100' : 'opacity-40'} size={16} />
        </button>
      )}

      {hasItems && (
        <button
          type="button"
          onClick={() => scrollerRef.current?.scrollBy({ left: SCROLL_BY_PX, behavior: 'smooth' })}
          className="absolute right-0 top-[52%] z-10 flex size-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 text-primary shadow"
          aria-label={`Scroll ${title} right`}
        >
          <ChevronRight className={canScrollRight ? 'opacity-100' : 'opacity-40'} size={16} />
        </button>
      )}
    </div>
  )
}

const AISearchResultsContent = ({ committedQuery }: AISearchResultsViewProps) => {
  const { data: searchResults } = useLynkSearch(committedQuery)
  const summary = searchResults.summary || ''
  const [typedSummary, setTypedSummary] = useState('')

  useEffect(() => {
    setTypedSummary('')
    if (!summary) return

    let index = 0
    const interval = window.setInterval(() => {
      index += 1
      setTypedSummary(summary.slice(0, index))
      if (index >= summary.length) {
        window.clearInterval(interval)
      }
    }, 14)

    return () => window.clearInterval(interval)
  }, [summary, committedQuery])

  const businessItems = searchResults.results?.businesses ?? []
  const userItems = searchResults.results?.users ?? []

  return (
    <div className="flex min-h-[300px] flex-col gap-5 pt-2">
      <div className="flex flex-row items-center gap-2">
        <AnimatedLynkIcon autoPlay={false} variant="gradient" width={26} height={26} />
        <p className="text-[14px] font-semibold text-primary">Lynk</p>
      </div>

      <p className="text-[13px] leading-6 text-primary">{typedSummary}</p>

      {businessItems.length > 0 && (
        <HorizontalSection title="Business">
          {businessItems.map((item) => (
            <div key={item.id} className="shrink-0 snap-start">
              <BusinessSearchResultItem business={item} />
            </div>
          ))}
        </HorizontalSection>
      )}

      {userItems.length > 0 && (
        <HorizontalSection title="Professionals">
          {userItems.map((item) => (
            <div key={item.id} className="shrink-0 snap-start">
              <LynkUserSearchResultItem user={item} />
            </div>
          ))}
        </HorizontalSection>
      )}
    </div>
  )
}

const AISearchResultsView = ({ committedQuery }: AISearchResultsViewProps) => {
  return (
    <Suspense fallback={<ThinkingState />}>
      <AISearchResultsContent committedQuery={committedQuery} />
    </Suspense>
  )
}

export default AISearchResultsView
